import { SequenceMessage } from '../types';

export interface SequenceMessageRowSnapshot {
  messageId: string;
  orderedIds: string[];
  rowYs: number[];
}

export interface SequenceMessageLike {
  id: string;
  y: number;
  label: string;
  sourceId: string;
  targetId: string;
}

export interface SequenceParticipantLike {
  id: string;
  x: number;
}

const DEFAULT_CHAR_WIDTH_ESTIMATE = 5.9;
const DEFAULT_LINE_HEIGHT = 14;
const DEFAULT_MIN_LABEL_WIDTH = 96;
const DEFAULT_SELF_LOOP_BELOW = 40;
const DEFAULT_NON_SELF_BELOW = 10;
const DEFAULT_ABOVE_GAP = 6;

export type SequenceGapResolver = (
  upper: Pick<SequenceMessageLike, 'label' | 'sourceId' | 'targetId'> & { fragmentId?: string },
  lower: Pick<SequenceMessageLike, 'label' | 'sourceId' | 'targetId'> & { fragmentId?: string },
) => number;

export function resolveSequenceMessageLabelWidth(
  participants: SequenceParticipantLike[],
  sourceId: string,
  targetId: string,
  minLabelWidth = DEFAULT_MIN_LABEL_WIDTH,
): number {
  const source = participants.find((participant) => participant.id === sourceId);
  if (!source) return minLabelWidth;

  const sourceCenter = source.x + 70;
  if (sourceId !== targetId) {
    const target = participants.find((participant) => participant.id === targetId);
    const lineSpan = target
      ? Math.max(40, Math.abs((target.x + 70) - sourceCenter))
      : 240;
    return Math.max(minLabelWidth, lineSpan - 12);
  }

  const sortedCenters = [...participants]
    .map((participant) => ({ id: participant.id, centerX: participant.x + 70 }))
    .sort((a, b) => a.centerX - b.centerX);
  const sourceIndex = sortedCenters.findIndex((entry) => entry.id === sourceId);
  if (sourceIndex < 0) return Math.max(140, minLabelWidth);

  const rightNeighbor = sortedCenters[sourceIndex + 1];
  const leftNeighbor = sortedCenters[sourceIndex - 1];
  const rightGap = rightNeighbor ? rightNeighbor.centerX - sourceCenter : undefined;
  const leftGap = leftNeighbor ? sourceCenter - leftNeighbor.centerX : undefined;
  const laneGap = rightGap ?? leftGap ?? 240;
  const availableWidth = Math.max(140, laneGap - 28);
  return Math.max(minLabelWidth, availableWidth);
}

export function estimateSequenceMessageFootprint(
  participants: SequenceParticipantLike[],
  label: string,
  sourceId: string,
  targetId: string,
  options?: {
    safeMargin?: number;
    charWidthEstimate?: number;
    lineHeight?: number;
    minLabelWidth?: number;
    selfBelow?: number;
    nonSelfBelow?: number;
    aboveGap?: number;
  },
): { above: number; below: number; maxLabelWidth: number; lineCount: number } {
  const safeMargin = options?.safeMargin ?? 0;
  const charWidthEstimate = options?.charWidthEstimate ?? DEFAULT_CHAR_WIDTH_ESTIMATE;
  const lineHeight = options?.lineHeight ?? DEFAULT_LINE_HEIGHT;
  const minLabelWidth = options?.minLabelWidth ?? DEFAULT_MIN_LABEL_WIDTH;
  const selfBelow = options?.selfBelow ?? DEFAULT_SELF_LOOP_BELOW;
  const nonSelfBelow = options?.nonSelfBelow ?? DEFAULT_NON_SELF_BELOW;
  const aboveGap = options?.aboveGap ?? DEFAULT_ABOVE_GAP;

  const maxLabelWidth = resolveSequenceMessageLabelWidth(participants, sourceId, targetId, minLabelWidth);
  const charsPerLine = Math.max(8, Math.floor((maxLabelWidth - 16) / charWidthEstimate));
  const lineCount = Math.max(
    1,
    (label || '')
      .split(/\r?\n/)
      .reduce((sum, rawLine) => {
        const line = rawLine.trim();
        if (!line) return sum + 1;
        return sum + Math.max(1, Math.ceil(line.length / charsPerLine));
      }, 0),
  );
  const labelHeight = lineCount * lineHeight + 6;
  return {
    above: labelHeight + aboveGap + safeMargin,
    below: (sourceId === targetId ? selfBelow : nonSelfBelow) + 2,
    maxLabelWidth,
    lineCount,
  };
}

export function nextSequenceMessageY(
  participants: SequenceParticipantLike[],
  messages: Array<Pick<SequenceMessageLike, 'y' | 'label' | 'sourceId' | 'targetId'>>,
  incoming: Pick<SequenceMessageLike, 'label' | 'sourceId' | 'targetId'>,
  options?: { safeMargin?: number; extraGap?: number; baseY?: number },
): number {
  const safeMargin = options?.safeMargin ?? 4;
  const extraGap = options?.extraGap ?? 8;
  const baseY = options?.baseY ?? 140;
  const incomingFp = estimateSequenceMessageFootprint(
    participants,
    incoming.label,
    incoming.sourceId,
    incoming.targetId,
    { safeMargin },
  );
  if (messages.length === 0) return baseY + incomingFp.above;
  const last = messages.reduce((top, message) => (message.y >= top.y ? message : top));
  const lastFp = estimateSequenceMessageFootprint(
    participants,
    last.label,
    last.sourceId,
    last.targetId,
    { safeMargin },
  );
  return last.y + lastFp.below + extraGap + incomingFp.above;
}

export function sortMessagesByY<T extends { y: number }>(messages: T[]): T[] {
  return [...messages].sort((a, b) => a.y - b.y);
}

export function assignSequenceMessageNumbers<T extends { id: string; y: number }>(
  messages: T[],
): Map<string, number> {
  const sorted = [...messages].sort((a, b) => a.y - b.y || a.id.localeCompare(b.id));
  const numbers = new Map<string, number>();
  sorted.forEach((message, index) => {
    numbers.set(message.id, index + 1);
  });
  return numbers;
}

export function buildMessageRowSnapshot(
  messages: SequenceMessageLike[],
  messageId: string,
): SequenceMessageRowSnapshot {
  const sorted = sortMessagesByY(messages);
  return {
    messageId,
    orderedIds: sorted.map((message) => message.id),
    rowYs: sorted.map((message) => message.y),
  };
}

export function resolveMessageInsertionY(
  messages: SequenceMessage[],
  proposedY: number,
  moving: Pick<SequenceMessageLike, 'label' | 'sourceId' | 'targetId'>,
  getGap: SequenceGapResolver,
  excludeMessageId?: string,
): number {
  const otherMessages = sortMessagesByY(messages.filter((message) => message.id !== excludeMessageId));
  let adjustedY = proposedY;
  let changed = true;
  let guard = 0;

  while (changed && guard < 200) {
    guard += 1;
    changed = false;
    for (const other of otherMessages) {
      if (adjustedY >= other.y) {
        const minGap = getGap(other, moving);
        if (adjustedY - other.y < minGap) {
          adjustedY = other.y + minGap;
          changed = true;
          break;
        }
      } else {
        const minGap = getGap(moving, other);
        if (other.y - adjustedY < minGap) {
          adjustedY = other.y - minGap;
          changed = true;
          break;
        }
      }
    }
  }

  return adjustedY;
}

export function computeSpacingUpdatesFromAnchor(
  messages: SequenceMessage[],
  getGap: SequenceGapResolver,
  anchorMessageId?: string,
): Array<{ id: string; y: number }> {
  const sorted = sortMessagesByY(messages);
  if (sorted.length < 2) return [];

  let startIdx = 1;
  if (anchorMessageId) {
    const anchorIdx = sorted.findIndex((message) => message.id === anchorMessageId);
    if (anchorIdx >= 1) startIdx = anchorIdx;
  }

  const pendingY = new Map<string, number>();
  const updates: Array<{ id: string; y: number }> = [];
  const getEffectiveY = (message: SequenceMessage) => pendingY.get(message.id) ?? message.y;

  for (let i = startIdx; i < sorted.length; i += 1) {
    const upper = sorted[i - 1];
    const lower = sorted[i];
    const minY = getEffectiveY(upper) + getGap(upper, lower);
    if (getEffectiveY(lower) < minY) {
      pendingY.set(lower.id, minY);
      updates.push({ id: lower.id, y: minY });
    }
  }

  return updates;
}

export function computePushDragUpdates(
  stateMessages: SequenceMessage[],
  snapshot: SequenceMessageRowSnapshot | null,
  messageId: string,
  proposedY: number,
  getGap: SequenceGapResolver,
): Array<{ id: string; y: number }> {
  const sorted = sortMessagesByY(stateMessages);
  const currentById = new Map(sorted.map((message) => [message.id, message]));

  const fallbackIdx = sorted.findIndex((message) => message.id === messageId);
  if (
    !snapshot ||
    snapshot.messageId !== messageId ||
    snapshot.orderedIds.length !== snapshot.rowYs.length ||
    fallbackIdx === -1
  ) {
    const dragMessage = sorted[fallbackIdx];
    let dragY = proposedY;
    if (fallbackIdx > 0) {
      const prev = sorted[fallbackIdx - 1];
      dragY = Math.max(dragY, prev.y + getGap(prev, dragMessage));
    }
    if (Math.abs(dragY - dragMessage.y) >= 0.5) {
      return [{ id: dragMessage.id, y: dragY }];
    }
    return [];
  }

  const dragIdx = snapshot.orderedIds.indexOf(messageId);
  if (dragIdx === -1) return [];
  const dragMessage = currentById.get(messageId);
  if (!dragMessage) return [];

  let dragY = proposedY;
  if (dragIdx > 0) {
    const prevId = snapshot.orderedIds[dragIdx - 1];
    const prevMessage = currentById.get(prevId);
    const prevY = snapshot.rowYs[dragIdx - 1];
    if (prevMessage) {
      dragY = Math.max(dragY, prevY + getGap(prevMessage, dragMessage));
    }
  }

  let pushDelta = 0;
  if (dragIdx < snapshot.orderedIds.length - 1) {
    const nextId = snapshot.orderedIds[dragIdx + 1];
    const nextMessage = currentById.get(nextId);
    if (nextMessage) {
      const nextBaseY = snapshot.rowYs[dragIdx + 1];
      const maxFreeY = nextBaseY - getGap(dragMessage, nextMessage);
      pushDelta = Math.max(0, dragY - maxFreeY);
    }
  }

  const updates: Array<{ id: string; y: number }> = [{ id: messageId, y: dragY }];
  if (dragIdx < snapshot.orderedIds.length - 1) {
    for (let idx = dragIdx + 1; idx < snapshot.orderedIds.length; idx += 1) {
      updates.push({
        id: snapshot.orderedIds[idx],
        y: snapshot.rowYs[idx] + pushDelta,
      });
    }
  }

  return updates.filter((update) => {
    const current = currentById.get(update.id);
    return current ? Math.abs(current.y - update.y) >= 0.5 : false;
  });
}

export function computeMultiPushDragUpdates(
  stateMessages: SequenceMessage[],
  snapshot: SequenceMessageRowSnapshot | null,
  messageId: string,
  selectedMessageIds: string[],
  proposedY: number,
  getGap: SequenceGapResolver,
): Array<{ id: string; y: number }> {
  const selectedSet = new Set(selectedMessageIds);
  if (selectedSet.size <= 1 || !selectedSet.has(messageId)) {
    return computePushDragUpdates(stateMessages, snapshot, messageId, proposedY, getGap);
  }

  if (
    !snapshot ||
    snapshot.messageId !== messageId ||
    snapshot.orderedIds.length !== snapshot.rowYs.length
  ) {
    return [];
  }

  const sourceIdx = snapshot.orderedIds.indexOf(messageId);
  if (sourceIdx === -1) return [];

  const anchorBaseY = snapshot.rowYs[sourceIdx];
  const delta = proposedY - anchorBaseY;
  const currentById = new Map(stateMessages.map((message) => [message.id, message]));
  const updates: Array<{ id: string; y: number }> = [];

  let previousMessage: SequenceMessage | null = null;
  let previousY: number | null = null;

  snapshot.orderedIds.forEach((id, idx) => {
    const current = currentById.get(id);
    if (!current) return;

    let nextY = selectedSet.has(id)
      ? snapshot.rowYs[idx] + delta
      : snapshot.rowYs[idx];

    if (previousMessage && previousY !== null) {
      nextY = Math.max(nextY, previousY + getGap(previousMessage, current));
    }

    if (Math.abs(current.y - nextY) >= 0.5) {
      updates.push({ id, y: nextY });
    }

    previousMessage = current;
    previousY = nextY;
  });

  return updates;
}

export function computeReorderDragUpdates(
  stateMessages: SequenceMessage[],
  snapshot: SequenceMessageRowSnapshot | null,
  messageId: string,
  proposedY: number,
): Array<{ id: string; y: number }> {
  if (
    !snapshot ||
    snapshot.messageId !== messageId ||
    snapshot.orderedIds.length !== snapshot.rowYs.length ||
    snapshot.rowYs.length < 2
  ) {
    return [];
  }

  let targetIdx = 0;
  for (let idx = 0; idx < snapshot.rowYs.length - 1; idx += 1) {
    const boundary = (snapshot.rowYs[idx] + snapshot.rowYs[idx + 1]) / 2;
    if (proposedY >= boundary) targetIdx = idx + 1;
  }

  const sourceIdx = snapshot.orderedIds.indexOf(messageId);
  if (sourceIdx === -1 || sourceIdx === targetIdx) return [];

  const reorderedIds = [...snapshot.orderedIds];
  reorderedIds.splice(sourceIdx, 1);
  reorderedIds.splice(targetIdx, 0, messageId);

  const currentById = new Map(stateMessages.map((message) => [message.id, message]));
  const updates: Array<{ id: string; y: number }> = [];
  reorderedIds.forEach((id, idx) => {
    const current = currentById.get(id);
    if (!current) return;
    const nextY = snapshot.rowYs[idx];
    if (Math.abs(current.y - nextY) >= 0.5) {
      updates.push({ id, y: nextY });
    }
  });
  return updates;
}

export function computeMultiReorderDragUpdates(
  stateMessages: SequenceMessage[],
  snapshot: SequenceMessageRowSnapshot | null,
  messageId: string,
  selectedMessageIds: string[],
  proposedY: number,
): Array<{ id: string; y: number }> {
  const selectedSet = new Set(selectedMessageIds);
  if (selectedSet.size <= 1 || !selectedSet.has(messageId)) {
    return computeReorderDragUpdates(stateMessages, snapshot, messageId, proposedY);
  }

  if (
    !snapshot ||
    snapshot.messageId !== messageId ||
    snapshot.orderedIds.length !== snapshot.rowYs.length ||
    snapshot.rowYs.length < 2
  ) {
    return [];
  }

  const sourceIdx = snapshot.orderedIds.indexOf(messageId);
  if (sourceIdx === -1) return [];

  const anchorBaseY = snapshot.rowYs[sourceIdx];
  const delta = proposedY - anchorBaseY;
  const selectedEntries: Array<{ id: string; sourceIdx: number; desiredY: number }> = [];
  const unselectedEntries: Array<{ id: string; sourceIdx: number; desiredY: number }> = [];

  snapshot.orderedIds.forEach((id, idx) => {
    const entry = {
      id,
      sourceIdx: idx,
      desiredY: selectedSet.has(id) ? snapshot.rowYs[idx] + delta : snapshot.rowYs[idx],
    };
    if (selectedSet.has(id)) {
      selectedEntries.push(entry);
    } else {
      unselectedEntries.push(entry);
    }
  });

  if (selectedEntries.length <= 1) {
    return computeReorderDragUpdates(stateMessages, snapshot, messageId, proposedY);
  }

  const reorderedIds: string[] = [];
  let selectedIdx = 0;
  let unselectedIdx = 0;
  while (selectedIdx < selectedEntries.length || unselectedIdx < unselectedEntries.length) {
    if (unselectedIdx >= unselectedEntries.length) {
      reorderedIds.push(selectedEntries[selectedIdx].id);
      selectedIdx += 1;
      continue;
    }
    if (selectedIdx >= selectedEntries.length) {
      reorderedIds.push(unselectedEntries[unselectedIdx].id);
      unselectedIdx += 1;
      continue;
    }

    const nextSelected = selectedEntries[selectedIdx];
    const nextUnselected = unselectedEntries[unselectedIdx];
    if (
      nextSelected.desiredY < nextUnselected.desiredY ||
      (
        Math.abs(nextSelected.desiredY - nextUnselected.desiredY) < 0.5 &&
        nextSelected.sourceIdx < nextUnselected.sourceIdx
      )
    ) {
      reorderedIds.push(nextSelected.id);
      selectedIdx += 1;
    } else {
      reorderedIds.push(nextUnselected.id);
      unselectedIdx += 1;
    }
  }

  const currentById = new Map(stateMessages.map((message) => [message.id, message]));
  const updates: Array<{ id: string; y: number }> = [];
  reorderedIds.forEach((id, idx) => {
    const current = currentById.get(id);
    if (!current) return;
    const nextY = snapshot.rowYs[idx];
    if (Math.abs(current.y - nextY) >= 0.5) {
      updates.push({ id, y: nextY });
    }
  });
  return updates;
}
